import React, { useState } from 'react';
import {
  ShieldCheck,
  AlertCircle,
  CheckCircle2,
  Phone,
  Lock,
  Mail,
  Loader2,
  ArrowRight,
  Clock,
  X,
  RefreshCw,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const GoogleAuthNoticeBanner: React.FC = () => {
  const { googleAuthNotice, setGoogleAuthNotice } = useApp();

  if (!googleAuthNotice) return null;

  const styles: Record<
    string,
    { bg: string; border: string; text: string; iconColor: string }
  > = {
    success: {
      bg: 'bg-emerald-50 dark:bg-emerald-950/50',
      border: 'border-emerald-200 dark:border-emerald-800',
      text: 'text-emerald-900 dark:text-emerald-200',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
    },
    cancelled: {
      bg: 'bg-slate-100 dark:bg-slate-800/80',
      border: 'border-slate-300 dark:border-slate-700',
      text: 'text-slate-800 dark:text-slate-200',
      iconColor: 'text-slate-600 dark:text-slate-400',
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-950/50',
      border: 'border-red-200 dark:border-red-900/60',
      text: 'text-red-800 dark:text-red-300',
      iconColor: 'text-red-600 dark:text-red-400',
    },
    phone_required: {
      bg: 'bg-amber-50 dark:bg-amber-950/50',
      border: 'border-amber-200 dark:border-amber-800',
      text: 'text-amber-900 dark:text-amber-200',
      iconColor: 'text-amber-600 dark:text-amber-400',
    },
    profile_required: {
      bg: 'bg-blue-50 dark:bg-blue-950/50',
      border: 'border-blue-200 dark:border-blue-800',
      text: 'text-blue-900 dark:text-blue-200',
      iconColor: 'text-blue-600 dark:text-blue-400',
    },
    approval_pending: {
      bg: 'bg-amber-50 dark:bg-amber-950/60',
      border: 'border-amber-300 dark:border-amber-800',
      text: 'text-amber-900 dark:text-amber-200',
      iconColor: 'text-amber-600 dark:text-amber-400',
    },
    linking_required: {
      bg: 'bg-indigo-50 dark:bg-indigo-950/50',
      border: 'border-indigo-200 dark:border-indigo-800',
      text: 'text-indigo-900 dark:text-indigo-200',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
    },
  };

  const currentStyle = styles[googleAuthNotice.type] || styles.error;

  return (
    <div
      className={`mb-5 p-4 rounded-2xl border ${currentStyle.bg} ${currentStyle.border} flex items-start justify-between gap-3 animate-in fade-in duration-200`}
      role="status"
    >
      <div className="flex items-start gap-2.5">
        {googleAuthNotice.type === 'success' ? (
          <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${currentStyle.iconColor}`} />
        ) : googleAuthNotice.type === 'approval_pending' ? (
          <Clock className={`w-4 h-4 shrink-0 mt-0.5 ${currentStyle.iconColor}`} />
        ) : (
          <AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${currentStyle.iconColor}`} />
        )}
        <div className={`text-xs leading-relaxed ${currentStyle.text}`}>
          <p className="font-extrabold">{googleAuthNotice.title}</p>
          <p className="mt-0.5 opacity-90">{googleAuthNotice.message}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setGoogleAuthNotice(null)}
        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg transition-colors cursor-pointer"
        aria-label="Dismiss notice"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export const GoogleAccountLinkingCard: React.FC = () => {
  const {
    pendingGoogleAuth,
    setPendingGoogleAuth,
    setCurrentUser,
    setGoogleAuthNotice,
    navigateTo,
    viewParams,
  } = useApp();

  const [verificationMethod, setVerificationMethod] = useState<'otp' | 'password'>('otp');
  const [otpChannel, setOtpChannel] = useState<'sms' | 'email'>('sms');
  const [sessionId, setSessionId] = useState<string>('');
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  if (!pendingGoogleAuth || pendingGoogleAuth.status !== 'requires_account_linking') {
    return null;
  }

  const email = pendingGoogleAuth.googleUser.email;
  const googleUserId = pendingGoogleAuth.googleUser.id;
  const maskedPhone = pendingGoogleAuth.existingAccount?.maskedPhone;

  const handleSendLinkingOtp = async (preferredChannel: 'sms' | 'email') => {
    setError(null);
    setInfoMessage(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/google/link-account/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          googleUserId,
          channel: preferredChannel,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Could not send verification code. Please try again.');
      }
      setSessionId(data.sessionId);
      setOtpSent(true);
      setInfoMessage(
        data.message ||
          `A 6-digit verification code has been sent to your registered ${
            data.channel === 'sms' ? 'phone number' : 'email address'
          }.`
      );
    } catch (err: any) {
      setError(err.message || 'Could not send verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, any> = {
        email,
        googleUserId,
      };
      if (verificationMethod === 'otp') {
        if (!sessionId || otpCode.trim().length < 6) {
          throw new Error('Please enter the 6-digit security code.');
        }
        payload.sessionId = sessionId;
        payload.otp = otpCode.trim();
      } else {
        if (!password) {
          throw new Error('Please enter your existing JobMatch NG account password.');
        }
        payload.password = password;
      }

      const res = await fetch('/api/auth/google/link-account/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Account linking verification failed.');
      }

      if (data.status === 'requires_phone_verification' && data.profile) {
        setPendingGoogleAuth({
          ...pendingGoogleAuth,
          status: 'requires_phone_verification',
          existingProfile: data.profile,
          message:
            data.message ||
            'Phone verification required. Please verify your Nigerian mobile number via SMS OTP to continue.',
        });
        setGoogleAuthNotice({
          type: 'phone_required',
          title: 'Phone verification required',
          message:
            data.message ||
            'Account linked! Please verify your Nigerian phone number via SMS OTP to continue.',
        });
        return;
      }

      if (data.profile) {
        setPendingGoogleAuth(null);
        setCurrentUser(data.profile);
        if (data.status === 'professional_approval_pending') {
          setGoogleAuthNotice({
            type: 'approval_pending',
            title: 'Professional approval pending',
            message:
              data.message ||
              'Your account is linked with Google. Your professional application is currently under administrator review.',
          });
          navigateTo('pro_dashboard');
          return;
        }
        setGoogleAuthNotice({
          type: 'success',
          title: 'Google login successful',
          message: 'Your existing JobMatch NG account is now securely linked with Google Sign-In.',
        });
        const target =
          viewParams.returnView ||
          (data.profile.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard');
        navigateTo(target);
      }
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6 p-5 sm:p-6 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/30 border-2 border-indigo-200 dark:border-indigo-800/80 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-xs">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white">
              Verify Ownership to Link Google Sign-In
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">
              An existing <strong>{pendingGoogleAuth.existingAccount?.existingRole || 'JobMatch NG'}</strong> account is already registered with{' '}
              <span className="font-bold">{email}</span>. For your security, we never automatically merge accounts without verifying ownership first.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setPendingGoogleAuth(null)}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
          title="Cancel account linking"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Method Selector */}
      <div className="flex p-1 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
        <button
          type="button"
          onClick={() => {
            setVerificationMethod('otp');
            setError(null);
          }}
          className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            verificationMethod === 'otp'
              ? 'bg-indigo-600 text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
          }`}
        >
          6-Digit Security Code (OTP)
        </button>
        <button
          type="button"
          onClick={() => {
            setVerificationMethod('password');
            setError(null);
          }}
          className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            verificationMethod === 'password'
              ? 'bg-indigo-600 text-white shadow-xs'
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
          }`}
        >
          Existing Password
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/60 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300 font-medium flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {infoMessage && (
        <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-800 dark:text-emerald-200 font-medium flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>{infoMessage}</span>
        </div>
      )}

      <form onSubmit={handleVerifyAndLink} className="space-y-3">
        {verificationMethod === 'otp' ? (
          <div className="space-y-3">
            {!otpSent ? (
              <div className="flex flex-col sm:flex-row gap-2">
                {maskedPhone && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => {
                      setOtpChannel('sms');
                      handleSendLinkingOtp('sms');
                    }}
                    className="flex-1 py-2.5 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-xs transition-all cursor-pointer disabled:opacity-60"
                  >
                    {loading && otpChannel === 'sms' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Phone className="w-3.5 h-3.5" />
                    )}
                    <span>Send SMS OTP ({maskedPhone})</span>
                  </button>
                )}
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    setOtpChannel('email');
                    handleSendLinkingOtp('email');
                  }}
                  className="flex-1 py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-xs transition-all cursor-pointer disabled:opacity-60"
                >
                  {loading && otpChannel === 'email' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Mail className="w-3.5 h-3.5" />
                  )}
                  <span>Send Email Security Code</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                    Enter 6-Digit Security Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-center text-base font-extrabold tracking-widest text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => handleSendLinkingOtp(otpChannel)}
                    className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <RefreshCw className="w-3 h-3" />
                    <span>Resend Code</span>
                  </button>
                  <button
                    type="submit"
                    disabled={loading || otpCode.length < 6}
                    className="py-2.5 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                    <span>Verify & Link Google Account</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Existing JobMatch NG Password for {email}
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your existing password"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading || !password}
              className="w-full py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              <span>Verify Password & Link Google Account</span>
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export const GooglePhoneVerificationCard: React.FC = () => {
  const {
    pendingGoogleAuth,
    setPendingGoogleAuth,
    setCurrentUser,
    setGoogleAuthNotice,
    navigateTo,
    viewParams,
  } = useApp();

  const [phone, setPhone] = useState<string>(
    pendingGoogleAuth?.existingProfile?.phone || ''
  );
  const [sessionId, setSessionId] = useState<string>('');
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  if (!pendingGoogleAuth || pendingGoogleAuth.status !== 'requires_phone_verification') {
    return null;
  }

  const userId = pendingGoogleAuth.existingProfile?.id || pendingGoogleAuth.googleUser.id;
  const email = pendingGoogleAuth.googleUser.email;
  const fullName = pendingGoogleAuth.existingProfile?.fullName || pendingGoogleAuth.googleUser.fullName || '';
  const avatarUrl = pendingGoogleAuth.existingProfile?.avatarUrl || pendingGoogleAuth.googleUser.avatarUrl || '';
  const requestedRole = pendingGoogleAuth.requestedRole || pendingGoogleAuth.existingProfile?.role || 'customer';

  const handleSendPhoneOtp = async () => {
    setError(null);
    setInfoMessage(null);
    if (!phone || phone.trim().length < 10) {
      setError('Please enter a valid Nigerian mobile phone number (e.g. 0803 123 4567).');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/google/verify-phone-existing/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          email,
          phone: phone.trim(),
          fullName,
          avatarUrl,
          role: requestedRole,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || 'Failed to send SMS verification code.');
      }
      setSessionId(data.sessionId);
      setOtpSent(true);
      setInfoMessage(data.message || 'A 6-digit SMS verification code has been sent to your phone.');
    } catch (err: any) {
      setError(err.message || 'Could not send SMS verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhoneOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!sessionId || otpCode.trim().length < 6) {
      setError('Please enter the 6-digit SMS OTP code.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/google/verify-phone-existing/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          email,
          phone: phone.trim(),
          sessionId,
          otp: otpCode.trim(),
          fullName,
          avatarUrl,
          role: requestedRole,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || data.error || 'Invalid or expired SMS OTP code.');
      }

      if (data.profile) {
        setPendingGoogleAuth(null);
        setCurrentUser(data.profile);
        try {
          localStorage.setItem('jobmatch_current_user', JSON.stringify(data.profile));
        } catch (_) {}
        if (data.status === 'professional_approval_pending') {
          setGoogleAuthNotice({
            type: 'approval_pending',
            title: 'Professional approval pending',
            message:
              'Phone number verified! Your professional application is currently under administrator review.',
          });
          navigateTo('pro_dashboard');
          return;
        }
        setGoogleAuthNotice({
          type: 'success',
          title: 'Google login successful',
          message: 'Your Nigerian phone number has been verified via SMS OTP.',
        });
        const target =
          viewParams.returnView ||
          (data.profile.role === 'professional' ? 'pro_dashboard' : 'customer_dashboard');
        navigateTo(target);
      }
    } catch (err: any) {
      setError(err.message || 'Phone verification failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-6 p-5 sm:p-6 rounded-2xl bg-amber-50/80 dark:bg-amber-950/40 border-2 border-amber-300 dark:border-amber-800 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-600 text-white flex items-center justify-center shrink-0 shadow-xs">
            <Phone className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white">
              Phone verification required
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">
              Your Google email (<span className="font-bold">{email}</span>) is verified, but JobMatch NG requires a verified Nigerian phone number via SMS OTP before accessing your dashboard.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setPendingGoogleAuth(null)}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/60 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300 font-medium flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {infoMessage && (
        <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-800 dark:text-emerald-200 font-medium flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>{infoMessage}</span>
        </div>
      )}

      <form onSubmit={handleVerifyPhoneOtp} className="space-y-3">
        <div>
          <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
            Nigerian Mobile Number
          </label>
          <div className="flex gap-2">
            <input
              type="tel"
              value={phone}
              disabled={otpSent}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 0803 123 4567"
              className="flex-1 px-3.5 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-60"
            />
            {!otpSent ? (
              <button
                type="button"
                disabled={loading}
                onClick={handleSendPhoneOtp}
                className="px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-xs transition-all cursor-pointer disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
                <span>Send SMS OTP</span>
              </button>
            ) : (
              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  setOtpSent(false);
                  setOtpCode('');
                }}
                className="px-3 py-2.5 rounded-xl bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold text-xs cursor-pointer"
              >
                Change
              </button>
            )}
          </div>
        </div>

        {otpSent && (
          <div className="space-y-3 pt-1">
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Enter 6-Digit SMS OTP Code
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-center text-base font-extrabold tracking-widest text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                disabled={loading}
                onClick={handleSendPhoneOtp}
                className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1 cursor-pointer"
              >
                <RefreshCw className="w-3 h-3" />
                <span>Resend SMS OTP</span>
              </button>
              <button
                type="submit"
                disabled={loading || otpCode.length < 6}
                className="py-2.5 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                <span>Verify Phone & Continue</span>
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};
